import { useState, useMemo, useCallback, memo, useContext } from "react";
import { useNavigate } from "react-router-dom";

import styled from "styled-components";

import { Button, Card } from "@aidron/aidron-ds";

import { i18n } from "../../../translate/i18n";

import { Default } from "../../../components/Default/Default";
import {
  CustomCard, 
  CustomHeading,
  CustomText,
  PageHeading 
} from "../../../components/StyledComponents/styleComponents.index";
import { 
  Copilot,
  ListItem,
  LoadingContent,
  ModalTemplate,
  ErrorModal,
  OpportunityOptions,
} from "../../../components/SharedComponents/sharedComponents.index";
import { LOADING_CONTENT_TYPE } from "../../../components/SharedComponents/LoadingContent/loading.content.constants";
import { SYSTEM_COLORS } from "../../../components/system.colors"
import { OPPORTUNITY_STATUS } from "../../../components/components.constants";

import PropTypes from "prop-types";

import { useOtbViewModel } from "../../../viewModel/useOtbVewModel";

import { currencyFormatter, createIntFormatter, textFormatter } from "../../../utils/formatter/formatter";

import { buildModalConfig, renderModal } from "../../../viewModel/builder";

import { RemovedOpportunitiesModal } from "./components/RemovedOppModal";

import { useAnalysisViewModel } from "../../../viewModel/useAnalysysViewModel";

import { TenantContext } from "../../../templates/context/TenantContext"

const { t } = i18n;

const language = localStorage.getItem('i18nextLng');

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: ${({ pointer }) => (pointer ? "pointer" : "")};
`;

const formatCurrency = (value) => currencyFormatter.format(value);
const formatNumber = (value) => createIntFormatter().format(value);

const getDisplayOrder = (counterName, type) => {
  const lowerName = counterName.toLowerCase();
  
  if (lowerName.includes('vendas') || lowerName.includes('sales')) return 1;
  if (type === 'avoidLoss' || type === 'additionalSale') return 1;
  
  if (lowerName.includes('compra') || lowerName.includes('purchase')) return 2;
  if (type === 'stock' || type === 'optimizeBuy') return 2;
  
  return 3;
};

export const OpportunityCounter = memo(function OpportunityCounter({ ctx, uiState }) {

  const navigate = useNavigate();

  const {
    checkoutOpportunities = [],
    opportunityData, 
    oppTypes = {}
  } = ctx || {};

  const { linkTo, page = 'base_opportunities' } = uiState || {};

  const isDetailed = page === 'detailed_opportunities';

  const checkoutSum = useMemo(
    () => checkoutOpportunities.reduce((acc, curr) => Number(acc) + Number(curr.value), 0),
    [checkoutOpportunities]
  );

  const dynamicCards = useMemo(() => {
    const cardsMap = new Map();

    Object.entries(oppTypes).forEach(([type, typeData]) => {
      const counterName = typeData.opportunitiCounterNamePt;
      const counterNameInEnglish = typeData.opportunitiCounterNameEn;
      
      if (!cardsMap.has(counterName)) {
        cardsMap.set(counterName, {
          title: counterName,
          englishTitle: counterNameInEnglish,
          types: [],
          displayOrder: getDisplayOrder(counterName, type),
        });
      }
      cardsMap.get(counterName).types.push(type);
    });

    return Array.from(cardsMap.values())
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map(card => ({
        ...card,
        valueToDisplay: opportunityData?.counter?.reduce((acc, curr) =>
          card.types.includes(curr.type) ? acc + curr.totalValue : acc, 0) || 0,
        countToDisplay: opportunityData?.counter?.reduce((acc, curr) =>
          card.types.includes(curr.type) ? acc + curr.count : acc, 0) || 0,
      }));
  }, [oppTypes, opportunityData?.counter]);

  const handleClick = useCallback(() => navigate(linkTo), [linkTo, navigate]);

  const sectionStyle = isDetailed
    ? { display: "flex", gap: 10, flexWrap: "wrap" }
    : { display: 'flex', gap: '20px' };

  const cardStyle = isDetailed
    ? { 
      backgroundColor: SYSTEM_COLORS.WHITE.WHITE, border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`, borderRadius: "12px", 
      boxShadow: `0 1px 3px ${SYSTEM_COLORS.BLACK.BLACK_4_PERCENT}`, padding: "20px 24px", flex: 1 
    }
    : {};

  const innerWrapperStyle = isDetailed
    ? {}
    : { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minInlineSize: '156px' };

  const labelStyle = isDetailed
    ? { fontSize: "11px", fontWeight: "600", color: "#999", letterSpacing: "0.5px", marginBottom: 8 }
    : { fontSize: '0.8rem', fontWeight: 'bold' };

  const headingFontSize = isDetailed ? ['32px', '32px', '32px'] : ['0.3rem', '0.7rem', '1.2rem'];
  const textFontSize = isDetailed ? ['12px', '12px', '12px'] : ['0.3rem', '0.6rem', '1.1rem'];
  const subPStyle = isDetailed ? { fontSize: "12px", color: SYSTEM_COLORS.GRAY.SONIC_SILVER } : { fontSize: '0.8rem' };

  return (
    <section style={sectionStyle}>

      <Card style={cardStyle}>
        <div style={innerWrapperStyle}>
          <p style={labelStyle}>
            {textFormatter(t("totalOpportunities"), { textTransform: 'uppercase'})}
          </p>

          <CustomHeading fontSize={headingFontSize} color={"orange"} fontWeight={isDetailed ? 700 : 500}>
            {formatCurrency(opportunityData?.totalValue || 0)}
          </CustomHeading>

          <Legend onClick={linkTo ? handleClick : () => {}} pointer={linkTo}>
            <CustomText fontSize={textFontSize} color={"orange"} weight="bold">
              {formatNumber(opportunityData?.count || 0)}
            </CustomText>

            <p style={subPStyle}>
              {opportunityData?.count === 1 ? t("opportunity").toLowerCase() : t("opportunities").toLowerCase()}
            </p>
          </Legend>
        </div>
      </Card>

      {dynamicCards.map((card, index) => {

        if (card.countToDisplay === 0) return null;

        return (
          <Card key={`dynamic-card-${card.title}-${index}`} style={cardStyle}>
            <div style={innerWrapperStyle}>
              <p style={labelStyle}>
                {language === 'pt-BR' 
                  ? textFormatter(card?.title, { textTransform: 'uppercase'}) 
                  : textFormatter(card.englishTitle, { textTransform: 'uppercase'}) 
                }
              </p>

              <CustomHeading fontSize={headingFontSize} color={"orange"} fontWeight={isDetailed ? 700 : 500} >
                {formatCurrency(card.valueToDisplay)}
              </CustomHeading>

              <Legend onClick={linkTo ? handleClick : () => {}} pointer={linkTo}>
                <CustomText fontSize={textFontSize} color={"orange"} weight="bold">
                  {formatNumber(card.countToDisplay)}
                </CustomText>

                <p style={subPStyle}>
                  {card.countToDisplay === 1 
                    ? textFormatter(t("opportunity"), { textTransform: 'lowercase' }) 
                    : textFormatter(t("opportunities"), { textTransform: 'lowercase' })
                  }
                </p>
              </Legend>
            </div>
          </Card>
        );
      })}      

      <Card style={cardStyle}>
        <div style={innerWrapperStyle}>
          <p style={labelStyle}>
            {textFormatter(t("inCheckout"), { textTransform: 'uppercase' })}
          </p>

          <CustomHeading fontSize={headingFontSize} color={"orangeGrey"} fontWeight={isDetailed ? 700 : 500}>
            {formatCurrency(checkoutSum)}
          </CustomHeading>

          <Legend onClick={linkTo ? handleClick : () => {}} pointer={checkoutOpportunities?.length > 0 ? "/acoes/checkout" : ""}>
            <CustomText fontSize={textFontSize} color={"orangeGrey"} weight="bold">
              {formatNumber(checkoutOpportunities?.length || 0)}
            </CustomText>

            <p style={subPStyle}>
              {checkoutOpportunities?.length === 1 
                ? textFormatter(t("opportunity"), { textTransform: 'lowercase' })
                : textFormatter(t("opportunities"), { textTransform: 'lowercase' })
              }
            </p>
          </Legend>
        </div>
      </Card>
    </section>
  );
});

export const Opportunity = () => {
  
  const { userInfo } = useContext(TenantContext)
  const { userId, tenant } = userInfo || {};
  const { tenantId } = tenant || {};

  const vm = useOtbViewModel({
    shouldFetchActiveOpp: OPPORTUNITY_STATUS.ACTIVE,
    shouldFetchCheckoutOpp: OPPORTUNITY_STATUS.CHECKOUT,
    shouldFetchRemovedOpp: OPPORTUNITY_STATUS.REMOVED
  });

  const vmAnalysis = useAnalysisViewModel({ 
    shouldFetchProducts: true,
    shouldFetchForecastData: true
  });

  const [autoFilterApplied, setAutoFilterApplied] = useState(false);
  const [openRemovedOppModal, setOpenRemovedOppModal] = useState(false);

  const clearAutoFilter = () => {
    setAutoFilterApplied(false);
    
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
  };

  const counterCtx = useMemo(() => ({
    opportunityData: vm.ctx.opportunityCounterData,
    checkoutOpportunities: vm.ctx.checkoutOpportunities?.checkoutOpportunities ?? [],
    oppTypes: vm.ctx.activeOpportunities?.opportunityTypes
  }), [
    vm.ctx.opportunityCounterData,
    vm.ctx.checkoutOpportunities?.checkoutOpportunities,
    vm.ctx.activeOpportunities?.opportunityTypes
  ]);

  const modals = useMemo(() => buildModalConfig({
    mode: 'opportunities',
    actions: {
      onClose: () => setOpenRemovedOppModal(false),
      onCloseError: () => vm?.actions?.resetOppError(),
    },
    ctx: {
      removedOpportunities: vm?.ctx?.removedOpportunities,
      opportunityTypes: vm?.ctx?.availableTypes,
      errorStatus: vm.uiState.activeOpportunitiesError?.response?.status 
        || vm.uiState.checkoutOpportunitiesError?.response?.status
        || vm.uiState.updateOppError?.response?.status
        || 500,
      errorDetails: vm.uiState.activeOpportunitiesError?.response?.data?.details
        || vm.uiState.checkoutOpportunitiesError?.response?.data?.details
        || vm.uiState.updateOppError?.response?.data?.errors
        || [],
      errorPage: 'opportunities',
      operationType: vm?.actions?.definePatchOperationType({ uiState: vm?.uiState?.updateOppError }),
      products: vmAnalysis?.ctx?.products
    },
    components: { ModalRemovedOpp: RemovedOpportunitiesModal, ErrorModal },
    uiState: { isOpen: openRemovedOppModal, isErrorOpen: !!vm?.uiState?.updateOppError },
  }), [
    vm?.ctx?.removedOpportunities,
    vm?.ctx?.availableTypes,
    vm.uiState.updateOppError,
    vm.uiState.activeOpportunitiesError,
    vm.uiState.checkoutOpportunitiesError,
    openRemovedOppModal,
    vm?.actions,
    vmAnalysis?.ctx?.products
  ]);

  if (vm.uiState.isLoadingActiveOpportunities || vm.uiState.isLoadingCheckoutOpportunities || vmAnalysis?.uiState?.isLoadingProducts)
    return <LoadingContent label={"Recuperando oportunidades..."} type={LOADING_CONTENT_TYPE.LOCAL_PAGE_STATE}/>

  return (
    <Default>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'end', paddingBlockStart: '1rem' }}>
        <div style={{ inlineSize: 'fit-content' }}>

          <Copilot ctx={{ userId: userId, tenantId: tenantId }} />
        </div>          

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          inlineSize: "100%",
          alignItems: 'center',
          marginBlockStart: '2%' }}>
          <PageHeading>{t("productOpportunities")}</PageHeading>
          <Opportunity.CounterOfCurrentOpportunities ctx={counterCtx} />
        </div>
     </div>

      {autoFilterApplied ? (
        <CustomCard margin="0 0 15px 0">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: "2%" }}>
            <Button label={t("showAllOpportunities")} onClick={clearAutoFilter} size="lg" />
          </div>
        </CustomCard>
      ) : (
        <OpportunityOptions
          actions={{
            setSelectedProductIds: vm.actions.setActiveOppProductIds,
            toggleTypeFilter: vm.actions.toggleTypeFilter,
            setOrder: vm.actions.setOrder,
          }}
          ctx={{
            productIds: vm.ctx.activeOpportunities?.activeProductIds,
            selectedTypes: vm.ctx.activeOpportunities?.types,
            selectedProductIds: vm.ctx.activeProductIds,
            typeOptions: vm.ctx.availableTypes,
            products: vmAnalysis?.ctx?.products
          }}
          uiState={{ order: vm.uiState.order, orderingMode: vm.uiState.orderOptions }}/>
      )}

      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <section style={{
          display: 'flex',
          alignItems: 'center',
          marginInlineEnd: '1.5rem',
          marginBlockEnd: '15px'
        }}>
          <CustomText weight="bold" margin="0 0.5rem 0 0">
            {t("removedOpportunities")}:
          </CustomText>
          <span
            onClick={vm.ctx.removedOpportunities.length ? () => setOpenRemovedOppModal(true) : undefined}
            style={{
              cursor: vm.ctx.removedOpportunities.length ? 'pointer' : 'default',
              color: vm.ctx.removedOpportunities.length ? '#e75d0d' : '#5f5e5e',
              textDecoration: vm.ctx.removedOpportunities.length ? 'underline' : 'none',
              fontWeight: 'bold',
            }}
          >
            {createIntFormatter().format(vm.ctx.removedOpportunities.length ?? 0)}
          </span>
        </section>
      </div>

      {renderModal(modals)}

      <div style={{ display: 'grid', gridTemplateRows: 'auto', gap: '10px' }}>
        {vm.uiState.sortedActiveOpportunities.map((opportunity) => (
          <ListItem
            actions={{ 
              handleRemoveOpportunity: vm?.actions?.handleRemoveOpportunity,
              resetModalState: vm?.actions?.resetModalState,
              setSelectedId: vm?.actions?.setSelectedId,
              setRemovalReason: vm?.actions?.setRemovalReason
            }}
            ctx={{
              opportunity: opportunity,
              activeOppsWithTypes: vm.ctx.activeOpportunities?.activeOpportunities,
              options: vm?.ctx?.getOptions,
              removalReason: vm?.ctx?.removalReason,
              selectedId: vm?.ctx?.selectedId,
              products: vmAnalysis?.ctx?.products
            }}
            uiState={{
              reactivateOpportunity: true,
              accordionButton: true
            }}
            key={opportunity.opportunityId} 
          />
        ))}        
      </div>

      <ModalTemplate open={!!vm?.ctx?.confirmationMessage} width="300px" closingFunction={vm?.actions?.resetModalState} heading=" ">
        <CustomHeading size="sm" margin="1.5rem 0 0 0" textAlign="center">
          {vm?.ctx?.confirmationMessage}
        </CustomHeading>
        <div style={{ display: 'flex', justifyContent: 'center', marginBlockStart: '1.5rem' }}>
          <Button label="Ok" onClick={vm?.actions?.resetModalState} />
        </div>
      </ModalTemplate>
    </Default>
  );
};

Opportunity.CounterOfCurrentOpportunities = OpportunityCounter;
OpportunityCounter.propTypes = { ctx: PropTypes.shape(), uiState: PropTypes.shape() };